/**
 * ReferralsTab.tsx
 *
 * Patient-detail referrals section matching legacy patient-referrals-list.php.
 * Two sections:
 *   1. Referral Sources — incoming referrals (who referred this patient)
 *   2. Referrals from Patient — outgoing referrals (plans this patient referred in)
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';
import { Spinner } from '@/components/ui';

interface PatientReferral {
  planId: number;
  planDisplayId: string | null;
  date: string | null;
  referralGeneralId: number;
  referralType: string;
  referralSpecific: string | null;
  refSourceId: number;
  referralFirstName: string;
  referralLastName: string;
  referralOther: string;
  sourceFirstName: string;
  sourceLastName: string;
}

interface OutgoingReferral {
  planId: number;
  planDisplayId: string | null;
  date: string | null;
  referredPatientId: number;
  firstName: string;
  lastName: string;
  totalPayments: number | null;
}

function getPatientReferrals(patientId: number): Promise<PatientReferral[]> {
  return apiClient
    .get<ApiResponse<PatientReferral[]>>(`/patients/${patientId}/referrals`)
    .then((r) => r.data.data);
}

function getReferralsFromPatient(patientId: number): Promise<OutgoingReferral[]> {
  return apiClient
    .get<ApiResponse<OutgoingReferral[]>>(`/patients/${patientId}/referrals/from-patient`)
    .then((r) => r.data.data);
}

function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Tiered source rendering, after patient-referrals-list.php:
 *   if no referralSpecific:
 *     - refSourceID resolved to a patient in this account → link to them
 *     - a stored referrer name with no resolvable patient → plain name
 *     - referralOther → plain text
 *     - else → —
 *   else: show the referralSpecific name
 *
 * The link is gated on the RESOLVED patient rather than on a non-zero refSourceId.
 * `plans.refSourceID` has held referral_level2 ids on rows written before the referral
 * columns were separated, and those numbers fall in the same range as real patientIDs
 * — so the old gate produced links to patient charts that do not exist.
 */
function renderSource(ref: PatientReferral): JSX.Element | string {
  if (!ref.referralSpecific) {
    // `sourceFirstName` is filled from the join on refSourceID, so it is non-empty
    // ONLY when that id really is a patient in this account. Linking on `refSourceId`
    // alone sent staff to /patients/<a lead source id> — a chart that does not exist,
    // which is how a referral row could open a page where nothing loads.
    if (ref.sourceFirstName && ref.refSourceId !== 0) {
      return (
        <Link to={`/patients/${ref.refSourceId}`} className="text-blue-600 hover:underline">
          {ref.sourceFirstName} {ref.sourceLastName}
        </Link>
      );
    }
    // A name with no resolvable patient behind it: show the name, offer no link.
    if (ref.referralFirstName) {
      return `${ref.referralFirstName} ${ref.referralLastName}`.trim();
    }
    if (ref.referralOther) return ref.referralOther;
    return '—';
  }
  return ref.referralSpecific;
}

/**
 * What to tell the reader when a panel will not load.
 *
 * Two outcomes need different remedies, and the old single line ("Failed to load
 * referrals.") gave neither: a 404 means this chart does not belong to the location the
 * user is signed in to, which they can fix themselves by switching location; anything
 * else is ours and needs a retry and then an admin. Deliberately says nothing about the
 * mechanism — the diagnosis lives in the log line the endpoints now write.
 */
function referralErrorMessage(error: unknown): string {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;

  if (status === 404) {
    return 'This patient is not available in the location you are signed in to. Switch locations, or ask your administrator to check the record.';
  }

  return 'Referrals could not be loaded. Refresh the page to try again — if it keeps happening, contact your system administrator.';
}

interface ReferralsTabProps {
  patientId: number;
}

export function ReferralsTab({ patientId }: ReferralsTabProps) {
  const incoming = useQuery({
    queryKey: ['patient-referrals', patientId],
    queryFn: () => getPatientReferrals(patientId),
  });

  const outgoing = useQuery({
    queryKey: ['patient-referrals-outgoing', patientId],
    queryFn: () => getReferralsFromPatient(patientId),
  });

  const incomingRows = incoming.data ?? [];
  const outgoingRows = outgoing.data ?? [];

  return (
    <div className="space-y-8 py-2">
      {/* Section 1: Referral Sources (incoming) */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Referral Sources</h2>

        {incoming.isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : incoming.isError ? (
          <p className="py-4 text-sm text-red-600">{referralErrorMessage(incoming.error)}</p>
        ) : incomingRows.length === 0 ? (
          <p className="py-4 text-sm text-slate-400 italic">No referrals found.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Plan #</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {incomingRows.map((ref) => (
                  <tr key={ref.planId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{formatShortDate(ref.date)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {ref.planDisplayId ? (
                        <Link to={`/plans/${ref.planId}`} className="text-blue-600 hover:underline">
                          {ref.planDisplayId}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-800">{ref.referralType}</td>
                    <td className="px-4 py-3 text-slate-700">{renderSource(ref)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Section 2: Referrals from Patient (outgoing) */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Referrals from Patient</h2>

        {outgoing.isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : outgoing.isError ? (
          <p className="py-4 text-sm text-red-600">{referralErrorMessage(outgoing.error)}</p>
        ) : outgoingRows.length === 0 ? (
          <p className="py-4 text-sm text-slate-400 italic">No referrals from this patient.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Plan #</th>
                  <th className="px-4 py-3">Patient</th>
                  <th className="px-4 py-3">Payments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {outgoingRows.map((ref) => (
                  <tr key={ref.planId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{formatShortDate(ref.date)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {ref.planDisplayId ? (
                        <Link to={`/plans/${ref.planId}`} className="text-blue-600 hover:underline">
                          {ref.planDisplayId}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {ref.referredPatientId ? (
                        <Link to={`/patients/${ref.referredPatientId}`} className="text-blue-600 hover:underline">
                          {ref.firstName} {ref.lastName}
                        </Link>
                      ) : (
                        `${ref.firstName} ${ref.lastName}`.trim() || '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-800">{formatCurrency(ref.totalPayments)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
